import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Users as UsersIcon, Tag as TagIcon, Film, Gauge, Server, Cloud, HardDrive, Megaphone, Calendar, Plus, Trash2, KeyRound, Crown, BookOpen, Pencil, Zap } from 'lucide-react';
import api from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { AdminCourses } from '../components/admin/AdminCourses';
import { LessonEditor } from '../components/admin/LessonEditor';
import { DifficultyBadge } from '../components/DifficultyBadge';

function providerOf(l) {
  return (l.bunny_video_id || l.cloudflare_stream_id || l.video_url) ? 'has' : 'none';
}
const humanize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="p-5">
      <Icon size={18} className="text-emerald" />
      <div className="mt-3 tnum text-2xl font-bold text-white">{value ?? '—'}</div>
      <div className="text-xs text-zinc-500">{label}</div>
    </Card>
  );
}

export default function Admin() {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [cf, setCf] = useState(null);
  const [r2, setR2] = useState(null);
  const [bunny, setBunny] = useState(null);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState('');
  const [lessonModal, setLessonModal] = useState(null); // {lesson?} for lessons tab editor

  // tag form
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#10B981');
  // broadcast form
  const [bTitle, setBTitle] = useState('');
  const [bMsg, setBMsg] = useState('');
  const [bBusy, setBBusy] = useState(false);
  // schedule form
  const [evTitle, setEvTitle] = useState('');
  const [evStart, setEvStart] = useState('');
  const [evType, setEvType] = useState('lesson');
  const [evUrl, setEvUrl] = useState('');
  const [evRecurrence, setEvRecurrence] = useState('none');
  const [evRecurrenceEnd, setEvRecurrenceEnd] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/admin/analytics').then((r) => r.data).catch(() => null),
      api.get('/admin/cloudflare/status').then((r) => r.data).catch(() => null),
      api.get('/admin/r2/status').then((r) => r.data).catch(() => null),
      api.get('/admin/bunny/status').then((r) => r.data).catch(() => null),
      api.get('/admin/courses').then((r) => r.data.courses || r.data || []).catch(() => []),
      api.get('/admin/users').then((r) => r.data.users || r.data || []).catch(() => []),
      api.get('/admin/tags').then((r) => r.data.tags || r.data || []).catch(() => []),
      api.get('/admin/lessons').then((r) => r.data.lessons || r.data || []).catch(() => []),
      api.get('/admin/schedule/events').then((r) => r.data.catalogue || r.data.events || r.data || []).catch(() => []),
    ]).then(([a, c, r, b, co, u, t, l, e]) => {
      setAnalytics(a); setCf(c); setR2(r); setBunny(b);
      setCourses(Array.isArray(co) ? co : []);
      setUsers(Array.isArray(u) ? u : []);
      setTags(Array.isArray(t) ? t : []);
      setLessons(Array.isArray(l) ? l : []);
      setEvents(Array.isArray(e) ? e : []);
      setLoading(false);
    });
  }, []);
  useEffect(load, [load]);

  const reloadCourses = useCallback(() => {
    api.get('/admin/courses').then((r) => setCourses(r.data.courses || [])).catch(() => {});
    api.get('/admin/lessons').then((r) => setLessons(r.data.lessons || [])).catch(() => {});
  }, []);

  const deleteLesson = async (id) => {
    if (!window.confirm('Delete this lesson permanently?')) return;
    try { await api.delete(`/admin/lessons/${id}`); toast.success('Lesson deleted'); reloadCourses(); }
    catch { toast.error('Could not delete lesson'); }
  };

  const togglePremium = async (id) => {
    try { const { data } = await api.put(`/admin/users/${id}/premium`); toast.success(`Premium ${data.is_premium ? 'granted' : 'revoked'}`); load(); }
    catch { toast.error('Could not update'); }
  };
  const resetPw = async (id) => {
    const pw = window.prompt('New password (min 8 chars):');
    if (!pw) return;
    try { await api.post(`/admin/users/${id}/reset-password`, { new_password: pw }); toast.success('Password reset'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not reset'); }
  };
  const createTag = async (e) => {
    e.preventDefault();
    if (!tagName.trim()) return;
    try { await api.post('/admin/tags', { name: tagName.trim(), color: tagColor }); setTagName(''); toast.success('Tag created'); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || 'Could not create tag'); }
  };
  const deleteTag = async (id) => {
    if (!window.confirm('Delete this tag?')) return;
    try { await api.delete(`/admin/tags/${id}`); toast.success('Tag deleted'); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || 'Could not delete'); }
  };
  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!bTitle.trim() || !bMsg.trim()) return toast.error('Title and message required');
    setBBusy(true);
    try {
      await api.post('/admin/notifications/broadcast', { title: bTitle.trim(), message: bMsg.trim(), audience: { kind: 'all' } });
      setBTitle(''); setBMsg(''); toast.success('Announcement sent to all members');
    } catch (er) { toast.error(er?.response?.data?.detail || 'Could not send'); }
    finally { setBBusy(false); }
  };
  const createEvent = async (e) => {
    e.preventDefault();
    if (!evTitle.trim() || !evStart) return toast.error('Title and start time required');
    const startDate = new Date(evStart);
    const recurrence = { freq: evRecurrence, interval: 1, byweekday: [] };
    if (evRecurrence === 'weekly') recurrence.byweekday = [(startDate.getDay() + 6) % 7];
    try {
      await api.post('/admin/schedule/events', {
        title: evTitle.trim(), start_time: startDate.toISOString(), event_type: evType,
        external_url: evUrl || null, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        status: 'active', audience: 'everyone',
        recurrence,
        recurrence_end: (evRecurrence !== 'none' && evRecurrenceEnd) ? evRecurrenceEnd : null,
      });
      setEvTitle(''); setEvStart(''); setEvUrl(''); setEvRecurrence('none'); setEvRecurrenceEnd('');
      toast.success('Event created'); load();
    } catch (er) { toast.error(er?.response?.data?.detail || 'Could not create event'); }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await api.delete(`/admin/schedule/events/${id}`); toast.success('Event deleted'); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || 'Could not delete'); }
  };

  if (loading) return <div className="grid h-[60vh] place-items-center"><Spinner className="h-8 w-8" /></div>;

  const analyticsEntries = analytics && typeof analytics === 'object' ? Object.entries(analytics).filter(([, v]) => typeof v === 'number' || typeof v === 'string') : [];
  const filteredLessons = lessons.filter((l) => !q || (l.title || '').toLowerCase().includes(q.toLowerCase()));

  const tabs = [
    ['overview', 'Overview', Gauge],
    ['courses', `Courses (${courses.length})`, BookOpen],
    ['users', `Members (${users.length})`, UsersIcon],
    ['lessons', `Lessons (${lessons.length})`, Film],
    ['tags', `Tags (${tags.length})`, TagIcon],
    ['schedule', `Schedule (${events.length})`, Calendar],
    ['broadcast', 'Announce', Megaphone],
  ];

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Admin</h1>
          <p className="mt-1 text-sm text-zinc-500">Manage your production Stock Method backend.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh"><RefreshCw size={15} /> Refresh</Button>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-white/10">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors ${tab === key ? 'border-emerald text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          {analyticsEntries.length > 0 && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {analyticsEntries.slice(0, 8).map(([k, v]) => <StatCard key={k} icon={Gauge} label={humanize(k)} value={String(v)} />)}
            </div>
          )}
          <Card className="p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-white"><Server size={18} className="text-emerald" /> Video infrastructure</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-white/5 bg-ink-surface p-4">
                <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-white"><Zap size={16} className="text-emerald" /> Video hosting</span><Badge tone={bunny?.configured ? 'emerald' : 'zinc'}>{bunny?.configured ? 'Connected' : 'Off'}</Badge></div>
                {bunny?.configured && <p className="mt-2 text-[11px] text-zinc-500">Library {bunny.library_id} · signed playback {bunny.token_auth_enabled ? 'on' : 'off'}</p>}
              </div>
              <div className="rounded-xl border border-white/5 bg-ink-surface p-4">
                <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-white"><Cloud size={16} /> Cloudflare Stream</span><Badge tone={cf?.configured ? 'emerald' : 'zinc'}>{cf?.configured ? 'Connected' : 'Off'}</Badge></div>
              </div>
              <div className="rounded-xl border border-white/5 bg-ink-surface p-4">
                <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-white"><HardDrive size={16} /> R2 storage</span><Badge tone={r2?.configured ? 'emerald' : 'zinc'}>{r2?.configured ? 'Connected' : 'Off'}</Badge></div>
              </div>
            </div>
            <p className="mt-4 text-xs text-zinc-500">Add a lesson video by pasting its video ID in the lesson editor. Length and thumbnail are pulled in automatically, and playback is signed on the backend.</p>
          </Card>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard icon={UsersIcon} label="Members" value={users.length} />
            <StatCard icon={Film} label="Lessons" value={lessons.length} />
            <StatCard icon={TagIcon} label="Tags" value={tags.length} />
            <StatCard icon={Crown} label="Premium members" value={users.filter((u) => u.is_premium).length} />
          </div>
        </div>
      )}

      {tab === 'users' && (
        <Card className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Role</th><th className="py-2 pr-4">Premium</th><th className="py-2 pr-4 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id || u.email} className="border-b border-white/5" data-testid={`admin-user-${u.user_id || u.email}`}>
                    <td className="py-3 pr-4 text-white">{u.name || '—'}</td>
                    <td className="py-3 pr-4 text-zinc-400">{u.email}</td>
                    <td className="py-3 pr-4">{u.is_admin ? <Badge tone="emerald">Admin</Badge> : <Badge tone="zinc">Member</Badge>}</td>
                    <td className="py-3 pr-4">{u.is_premium ? <Badge tone="amber">Premium</Badge> : <span className="text-zinc-600">—</span>}</td>
                    <td className="py-3 pr-4">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => togglePremium(u.user_id)} data-testid={`user-premium-${u.user_id}`}><Crown size={14} /> {u.is_premium ? 'Revoke' : 'Grant'}</Button>
                        <Button size="sm" variant="ghost" onClick={() => resetPw(u.user_id)} data-testid={`user-reset-${u.user_id}`}><KeyRound size={14} /> Reset</Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-500">No members returned.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'courses' && (
        <AdminCourses courses={courses} reloadCourses={reloadCourses} />
      )}

      {tab === 'lessons' && (
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <Input placeholder="Search lessons…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64" data-testid="admin-lesson-search" />
            <Button variant="emerald" size="sm" onClick={() => setLessonModal({})} data-testid="admin-new-lesson"><Plus size={16} /> New lesson</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-zinc-500"><th className="py-2 pr-4">Title</th><th className="py-2 pr-4">Tags</th><th className="py-2 pr-4">Level</th><th className="py-2 pr-4">Video</th><th className="py-2 pr-4 text-right">Actions</th></tr></thead>
              <tbody>
                {filteredLessons.map((l) => (
                    <tr key={l.lesson_id} className="border-b border-white/5" data-testid={`admin-lesson-${l.lesson_id}`}>
                      <td className="py-3 pr-4 text-white">{l.title}</td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {(l.tags || []).length ? l.tags.map((t) => {
                            const c = (tags.find((x) => x.name === t) || {}).color;
                            const style = (c && c[0] === '#') ? { color: c, borderColor: `${c}66`, backgroundColor: `${c}1f` } : null;
                            return (
                              <span key={t} style={style || undefined}
                                className={style ? 'rounded-full border px-2 py-0.5 text-[11px] font-medium' : 'rounded-full border border-emerald/30 bg-emerald/10 px-2 py-0.5 text-[11px] font-medium text-emerald'}>
                                {t}
                              </span>
                            );
                          }) : <span className="text-zinc-600">—</span>}
                        </div>
                      </td>
                      <td className="py-3 pr-4"><DifficultyBadge level={l.difficulty} /></td>
                      <td className="py-3 pr-4">{providerOf(l) === 'has' ? <Badge tone="emerald">Video</Badge> : <Badge tone="zinc">No video</Badge>}</td>
                      <td className="py-3 pr-4">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setLessonModal({ lesson: l })} data-testid={`admin-lesson-edit-${l.lesson_id}`}><Pencil size={14} /> Edit</Button>
                          <button onClick={() => deleteLesson(l.lesson_id)} className="text-zinc-500 hover:text-red-400" data-testid={`admin-lesson-delete-${l.lesson_id}`}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                ))}
                {filteredLessons.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-500">No lessons.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'tags' && (
        <div className="space-y-4">
          <Card className="p-6">
            <form onSubmit={createTag} className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]"><label className="mb-1 block text-xs text-zinc-500">New tag name</label><Input value={tagName} onChange={(e) => setTagName(e.target.value)} placeholder="e.g. Options" data-testid="admin-tag-name" /></div>
              <div><label className="mb-1 block text-xs text-zinc-500">Color</label><input type="color" value={tagColor} onChange={(e) => setTagColor(e.target.value)} className="h-11 w-14 rounded-lg border border-zinc-800 bg-ink-surface" data-testid="admin-tag-color" /></div>
              <Button type="submit" variant="emerald" data-testid="admin-tag-create"><Plus size={16} /> Create</Button>
            </form>
          </Card>
          <Card className="p-6">
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <span key={t.tag_id || t.name} className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm" style={{ borderColor: `${t.color || '#3f3f46'}66`, color: t.color || '#d4d4d8' }} data-testid={`admin-tag-${t.tag_id || t.name}`}>
                  {t.name}{typeof t.count === 'number' && <span className="tnum text-xs opacity-70">{t.count}</span>}
                  <button onClick={() => deleteTag(t.tag_id)} className="text-zinc-500 hover:text-red-400" data-testid={`admin-tag-delete-${t.tag_id || t.name}`}><Trash2 size={13} /></button>
                </span>
              ))}
              {tags.length === 0 && <p className="text-sm text-zinc-500">No tags yet.</p>}
            </div>
          </Card>
        </div>
      )}

      {tab === 'schedule' && (
        <div className="space-y-4">
          <Card className="p-6">
            <form onSubmit={createEvent} className="grid gap-3 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs text-zinc-500">Title</label><Input value={evTitle} onChange={(e) => setEvTitle(e.target.value)} data-testid="admin-event-title" /></div>
              <div><label className="mb-1 block text-xs text-zinc-500">Start time</label><Input type="datetime-local" value={evStart} onChange={(e) => setEvStart(e.target.value)} data-testid="admin-event-start" /></div>
              <div>
                <label className="mb-1 block text-xs text-zinc-500">Type</label>
                <select value={evType} onChange={(e) => setEvType(e.target.value)} className="h-11 w-full rounded-xl border border-zinc-800 bg-ink-surface px-3 text-sm text-white" data-testid="admin-event-type">
                  <option value="lesson">Lesson</option><option value="live_trading">Live Trading</option>
                </select>
              </div>
              <div><label className="mb-1 block text-xs text-zinc-500">Join URL (optional)</label><Input value={evUrl} onChange={(e) => setEvUrl(e.target.value)} placeholder="https://…" data-testid="admin-event-url" /></div>
              <div>
                <label className="mb-1 block text-xs text-zinc-500">Repeat</label>
                <select value={evRecurrence} onChange={(e) => setEvRecurrence(e.target.value)} className="h-11 w-full rounded-xl border border-zinc-800 bg-ink-surface px-3 text-sm text-white" data-testid="admin-event-recurrence">
                  <option value="none">Does not repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekdays">Every weekday (Mon–Fri)</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {evRecurrence !== 'none' && (
                <div><label className="mb-1 block text-xs text-zinc-500">Repeat until (optional)</label><Input type="date" value={evRecurrenceEnd} onChange={(e) => setEvRecurrenceEnd(e.target.value)} data-testid="admin-event-recurrence-end" /></div>
              )}
              <div className="sm:col-span-2"><Button type="submit" variant="emerald" data-testid="admin-event-create"><Plus size={16} /> Create event</Button></div>
            </form>
          </Card>
          <Card className="p-6">
            {events.length === 0 ? <p className="text-sm text-zinc-500">No events scheduled.</p> : (
              <div className="space-y-2">
                {events.map((e, i) => (
                  <div key={e.event_id || i} className="flex items-center gap-4 rounded-xl border border-white/5 bg-ink-surface px-4 py-3" data-testid={`admin-event-${e.event_id || i}`}>
                    <div className="min-w-0 flex-1"><div className="text-white">{e.title}</div><div className="text-xs text-zinc-500">{e.start_time ? new Date(e.start_time).toLocaleString() : ''} · {e.event_type}</div></div>
                    <button onClick={() => deleteEvent(e.event_id)} className="text-zinc-500 hover:text-red-400" data-testid={`admin-event-delete-${e.event_id || i}`}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'broadcast' && (
        <Card className="mx-auto max-w-xl p-6">
          <div className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-white"><Megaphone size={18} className="text-emerald" /> Send announcement</div>
          <form onSubmit={sendBroadcast} className="space-y-3">
            <Input placeholder="Title" value={bTitle} onChange={(e) => setBTitle(e.target.value)} data-testid="broadcast-title" />
            <Textarea rows={4} placeholder="Message to all members…" value={bMsg} onChange={(e) => setBMsg(e.target.value)} data-testid="broadcast-message" />
            <Button type="submit" variant="emerald" className="w-full" disabled={bBusy} data-testid="broadcast-send">{bBusy ? 'Sending…' : 'Send to all members'}</Button>
          </form>
        </Card>
      )}
      {lessonModal && (
        <LessonEditor
          lesson={lessonModal.lesson}
          courses={courses}
          onClose={() => setLessonModal(null)}
          onSaved={reloadCourses}
        />
      )}
    </div>
  );
}
